import React from "react";
import { Box, Button, Grid, TextField } from "@mui/material";
import ReactMarkdown from "react-markdown";
import MDEditor from "@uiw/react-md-editor";

interface SummaryEditorProps {
  summaryContent: string;
  shortContent?: string;
  onSummaryChange: (value: string) => void;
  onShortContentChange?: (value: string) => void;
  onGenerateClick: () => void;
  isLoading: boolean;
  generateButtonText: string;
  loadingText: string;
  type: string;
}

/**
 * A reusable component for editing and previewing summary content
 *
 * @param summaryContent - The current summary content
 * @param onSummaryChange - Function to handle content changes
 * @param onGenerateClick - Function to handle generate button click
 * @param isLoading - Whether the generation is in progress
 * @param generateButtonText - Text to display on the generate button
 * @param loadingText - Text to display when loading
 */
const SummaryEditor: React.FC<SummaryEditorProps> = ({
  summaryContent,
  shortContent,
  onSummaryChange,
  onShortContentChange,
  onGenerateClick,
  isLoading,
  generateButtonText,
  loadingText,

  type,
}) => {
  return (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Box sx={{ mb: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onGenerateClick}
            disabled={isLoading}
          >
            {isLoading ? loadingText : generateButtonText}
          </Button>
        </Box>
        <Box>
          <Box data-color-mode="light">
            <TextField
              fullWidth
              label="Long Content"
              multiline
              rows={11.75}
              value={summaryContent}
              onChange={(e) => onSummaryChange(e.target.value)}
            />
            {type === "post_match" && (
              <TextField
                fullWidth
                label="Short Content"
                sx={{ mt: 3 }}
                multiline
                rows={8.75}
                value={shortContent}
                onChange={(e) => onShortContentChange?.(e.target.value)}
              />
            )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box sx={{ height: "50px" }}></Box>
        {summaryContent && (
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              maxHeight: "300px",
              overflow: "auto",
            }}
          >
            <Box
              sx={{
                p: 2,
                textTransform: "none",
                fontStyle: "normal",
                borderRadius: 1,
                "& > *": {
                  textTransform: "none",
                  fontSize: 16,
                  fontWeight: 400,
                  fontFamily: "SF UI Text",
                  lineHeight: "22px",
                  fontStyle: "normal !important",
                },
              }}
            >
              <ReactMarkdown>{summaryContent}</ReactMarkdown>
            </Box>
          </Box>
        )}
        {shortContent && (
          <Box sx={{ mt: 3.5 }}>
            <Box
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 1,
                maxHeight: "300px",
                overflow: "auto",
              }}
            >
              <Box
                sx={{
                  p: 2,
                  textTransform: "none",
                  fontStyle: "normal",
                  borderRadius: 1,
                  "& > *": {
                    textTransform: "none",
                    fontSize: 16,
                    fontWeight: 400,
                    fontFamily: "SF UI Text",
                    lineHeight: "22px",
                    fontStyle: "normal !important",
                  },
                }}
              >
                <ReactMarkdown>{shortContent}</ReactMarkdown>
              </Box>
            </Box>
          </Box>
        )}
      </Grid>
    </Grid>
  );
};

export default SummaryEditor;
